import { useState, useEffect, useRef, useCallback, useContext, createContext, Children } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Brain, Mail, Twitter, Slack, Github, GitBranch, Chrome, Figma, Trello, Youtube,
  Instagram, Linkedin,
  Plug, X, EyeOff, Eye, LogOut, MessageCircle, Send, Webhook, Search,
  Loader2, Check, CheckCircle2, ChevronDown, ExternalLink, Zap, MoreHorizontal, Database, HardDrive, Cloud, ArrowRight, ArrowUpDown,
} from "lucide-react";
import { useAI } from "../context/AIContext";
import { twitterAPI, youtubeAPI, integrationRequestAPI, gmailAPI, gitAPI, calendarAPI, instagramAPI, linkedinAPI, dataConnectionsAPI, API_ORIGIN } from "../services/api";
import { isChannelConnected } from "../services/notificationChannels";
import toast from "react-hot-toast";

// ─── Theme ────────────────────────────────────────────────────────────────────
const blue    = "var(--lyra-color-primary)";
const tx      = "var(--lyra-color-text)";
const mu      = "var(--app-text-3)";
const su      = "var(--lyra-color-text-muted)";
const bord    = "var(--lyra-color-border)";

// ─── Page context: per-tile metadata + shared data-connection state ───────────
const IntegrationCtx = createContext({ category: "", popular: false, docsUrl: "", dataConnections: [], refresh: () => {} });

// ─── "…" menu ─────────────────────────────────────────────────────────────────
function CardMenu({ docsUrl }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  if (!docsUrl) return null;
  return (
    <div className="int-kebab" ref={ref}>
      <button type="button" className="int-kebab-btn" aria-label="More options" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(v => !v)}>
        <MoreHorizontal size={15} />
      </button>
      {open && (
        <div className="int-menu" role="menu">
          <a role="menuitem" href={docsUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <ExternalLink size={13} /> Documentation
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Card shell: the first child is the header, the rest are the actions ──────
function IntCard({ children, connected }) {
  const { docsUrl } = useContext(IntegrationCtx);
  const [head, ...rest] = Children.toArray(children);
  return (
    <div className={`int-card${connected ? " connected" : ""}`}>
      {head}
      {connected && <span className="int-connected"><i />Connected</span>}
      <div className="int-actions">
        {rest}
        <CardMenu docsUrl={docsUrl} />
      </div>
    </div>
  );
}

// ─── Card header (icon + name + desc + category pill) ─────────────────────────
function CardHeader({ icon, iconBg, name, desc, connected }) {
  const { category, popular } = useContext(IntegrationCtx);
  return (
    <div className="int-head">
      <div className="int-icon" style={{ background: iconBg }}>{icon}</div>
      <div className="int-text">
        <div className="int-name">{name}</div>
        <div className="int-desc">{desc}</div>
        {!connected && (popular || category) && <span className={`int-pill${popular ? " popular" : ""}`}>{popular ? "Popular" : category}</span>}
      </div>
    </div>
  );
}

function Divider() {
  return null;
}

// ─── Btn helpers ──────────────────────────────────────────────────────────────
function ConnectBtn({ onClick, loading, label = "Connect", tone }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} className={`int-btn primary${tone === "accent" ? " accent" : ""}`}>
      {loading && <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} />}
      {loading ? "Connecting…" : label}
    </button>
  );
}

function DisconnectBtn({ onClick, loading }) {
  return (
    <button onClick={onClick} disabled={loading} className="int-btn danger" aria-label="Disconnect" title="Disconnect">
      {loading ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <LogOut size={13} />}
      <span className="int-btn-label">{loading ? "Removing…" : "Disconnect"}</span>
    </button>
  );
}

// ─── Modal shell for credential forms ─────────────────────────────────────────
function FormModal({ title, subtitle, onClose, children }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return createPortal(
    <div className="int-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="int-modal" role="dialog" aria-label={title}>
        <div className="int-modal-head">
          <div>
            <div className="int-modal-title">{title}</div>
            {subtitle && <div className="int-modal-sub">{subtitle}</div>}
          </div>
          <button type="button" className="int-kebab-btn" aria-label="Close" onClick={onClose}><X size={15} /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function InputField({ type = "text", value, onChange, placeholder, accentColor, onKeyDown, rightSlot }) {
  return (
    <div style={{ position: "relative" }}>
      <input type={type} value={value} onChange={onChange} onKeyDown={onKeyDown} placeholder={placeholder}
        // These hold API keys and bot tokens, never a login: stop the browser/password managers from autofilling saved credentials
        autoComplete={type === "password" ? "new-password" : "off"} data-lpignore="true" data-1p-ignore="true" data-form-type="other"
        style={{ width: "100%", padding: rightSlot ? "9px 36px 9px 12px" : "9px 12px", border: `1.5px solid ${bord}`, borderRadius: 9, fontSize: 13, color: tx, outline: "none", fontFamily: "inherit", background: "var(--lyra-color-surface-2)", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = accentColor || blue}
        onBlur={e => e.target.style.borderColor = bord} />
      {rightSlot && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>{rightSlot}</div>
      )}
    </div>
  );
}

// ─── Modal: Twitter credentials ───────────────────────────────────────────────
const TWITTER_FIELD_GROUPS = [
  {
    title: "App credentials",
    subtitle: "From your app's \"Keys & Tokens\" tab, under OAuth 1.0 Keys",
    fields: [
      { key: "apiKey", label: "API Key", xName: "Consumer Key" },
      { key: "apiSecret", label: "API Secret", xName: "Consumer Secret" },
    ],
  },
  {
    title: "Your access tokens",
    subtitle: "Generated for your own account, same page",
    fields: [
      { key: "accessToken", label: "Access Token", xName: "Access Token" },
      { key: "accessSecret", label: "Access Token Secret", xName: "Access Token Secret" },
    ],
  },
];

export function TwitterCredModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ apiKey: "", apiSecret: "", accessToken: "", accessSecret: "" });
  const [show, setShow] = useState({ apiKey: false, apiSecret: false, accessToken: false, accessSecret: false });
  const [saving, setSaving] = useState(false);
  const allFilled = Object.values(form).every(v => v.trim());

  async function handleSave() {
    if (!allFilled) { toast.error("All four fields are required."); return; }
    setSaving(true);
    try {
      const res = await twitterAPI.saveCredentials({ apiKey: form.apiKey.trim(), apiSecret: form.apiSecret.trim(), accessToken: form.accessToken.trim(), accessSecret: form.accessSecret.trim() });
      toast.success(`Connected as @${res.username}!`);
      onSaved({ username: res.username, name: res.name });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--lyra-color-surface)", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ background: "linear-gradient(135deg,#1DA1F2,#0d8cd6)", padding: "20px 24px", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 1 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Twitter size={22} color="var(--app-on-accent)" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--app-on-accent)" }}>Connect Twitter / X</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.75)" }}>Credentials are encrypted at rest — verified live before saving</p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", borderRadius: 8, padding: 6, display: "flex", flexShrink: 0 }}><X size={16} color="var(--app-on-accent)" /></button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Prerequisites */}
          <div style={{ background: "var(--app-primary-soft)", border: "1px solid var(--app-primary-soft-2)", borderRadius: 12, padding: "12px 14px" }}>
            <p style={{ margin: "0 0 7px", fontWeight: 700, fontSize: 12.5, color: "var(--app-primary-deep)" }}>Before you start</p>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#075985", lineHeight: 1.65 }}>
              <li>Create (or open) an app in the X Developer Console</li>
              <li>Under <strong>User authentication settings</strong>, enable OAuth 1.0a with <strong>Read and Write</strong> permission</li>
              <li>Changed permissions on an existing app? <strong>Regenerate</strong> your Access Token — old tokens keep the old (read-only) scope</li>
              <li>Copy the four values below from the app's <strong>Keys &amp; Tokens</strong> tab</li>
            </ol>
            <a href="https://console.x.com" target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 9, fontSize: 12, fontWeight: 600, color: "var(--app-primary-deep)", textDecoration: "none" }}>
              Open X Developer Console <ExternalLink size={12} />
            </a>
          </div>

          {TWITTER_FIELD_GROUPS.map(group => (
            <div key={group.title}>
              <p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: 12.5, color: tx }}>{group.title}</p>
              <p style={{ margin: "0 0 10px", fontSize: 11.5, color: mu }}>{group.subtitle}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {group.fields.map(({ key, label, xName }) => (
                  <div key={key}>
                    <label style={{ display: "flex", alignItems: "baseline", gap: 6, fontWeight: 600, fontSize: 13, color: "var(--app-text-2)", marginBottom: 5 }}>
                      {label}
                      <span style={{ fontWeight: 400, fontSize: 11, color: mu }}>({xName} in X console)</span>
                    </label>
                    <InputField type={show[key] ? "text" : "password"} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={`Paste your ${xName.toLowerCase()}`} accentColor="#1DA1F2"
                      rightSlot={<button type="button" onClick={() => setShow(s => ({ ...s, [key]: !s[key] }))} style={{ background: "none", border: "none", cursor: "pointer", color: mu, padding: 0, display: "flex" }}>{show[key] ? <EyeOff size={14} /> : <Eye size={14} />}</button>} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button onClick={handleSave} disabled={saving || !allFilled} style={{ background: saving ? "#93C5FD" : !allFilled ? "#BFE0F7" : "#1DA1F2", color: "var(--app-on-accent)", border: "none", borderRadius: 10, padding: "11px 0", fontWeight: 700, fontSize: 14, cursor: (saving || !allFilled) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit", transition: "background 0.15s" }}>
            {saving ? <><Loader2 size={15} style={{ animation: "spin 0.7s linear infinite" }} />Verifying with X…</> : "Save & Verify Credentials"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Gmail compose ─────────────────────────────────────────────────────
function GmailComposeModal({ fromEmail, onClose }) {
  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function handleSend() {
    if (!form.to.trim() || !form.subject.trim() || !form.body.trim()) { toast.error("All fields required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.to.trim())) { toast.error("Enter a valid email."); return; }
    setSending(true);
    try {
      await gmailAPI.send({ to: form.to.trim(), subject: form.subject.trim(), body: form.body.trim() });
      toast.success(`Email sent to ${form.to.trim()}!`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally { setSending(false); }
  }
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--lyra-color-surface)", borderRadius: 20, width: "100%", maxWidth: 500, boxShadow: "0 24px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg,#EA4335,#c5221f)", padding: "20px 24px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}><Mail size={22} color="var(--app-on-accent)" /></div>
          <div style={{ flex: 1 }}><p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--app-on-accent)" }}>Compose Email</p><p style={{ margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.75)" }}>From: {fromEmail}</p></div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", borderRadius: 8, padding: 6, display: "flex" }}><X size={16} color="var(--app-on-accent)" /></button>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {[["to", "email", "recipient@example.com"], ["subject", "text", "Email subject"]].map(([k, t, ph]) => (
            <div key={k}>
              <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: "var(--app-text-2)", marginBottom: 5 }}>{k.charAt(0).toUpperCase() + k.slice(1)}</label>
              <InputField type={t} value={form[k]} onChange={set(k)} placeholder={ph} accentColor="#EA4335" />
            </div>
          ))}
          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: "var(--app-text-2)", marginBottom: 5 }}>Message</label>
            <textarea placeholder="Write your message here…" value={form.body} onChange={set("body")} rows={5}
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid var(--lyra-color-border)", borderRadius: 9, fontSize: 13, color: tx, background: "var(--lyra-color-surface-2)", boxSizing: "border-box", outline: "none", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5 }}
              onFocus={e => e.target.style.borderColor = "#EA4335"} onBlur={e => e.target.style.borderColor = "var(--lyra-color-border)"} />
          </div>
          <button onClick={handleSend} disabled={sending} style={{ background: sending ? "#F28B84" : "#EA4335", color: "var(--app-on-accent)", border: "none", borderRadius: 10, padding: "11px 0", fontWeight: 700, fontSize: 14, cursor: sending ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" }}>
            {sending ? <><Loader2 size={15} style={{ animation: "spin 0.7s linear infinite" }} />Sending…</> : <><Mail size={15} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Twitter card ─────────────────────────────────────────────────────────────
function TwitterCard() {
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  useEffect(() => { checkStatus(); }, []);
  async function checkStatus() { try { const d = await twitterAPI.credentialsStatus(); setStatus(d); } catch { setStatus({ configured: false }); } }
  async function handleDisconnect() {
    setDisconnecting(true);
    try { await twitterAPI.removeCredentials(); setStatus({ configured: false }); toast.success("Twitter removed."); }
    catch (err) { toast.error(err.message); }
    finally { setDisconnecting(false); }
  }
  const loading = status === null;
  const connected = status?.configured;
  return (
    <>
      <IntCard connected={connected}>
        <CardHeader icon={<Twitter size={20} />} iconBg="#1DA1F215" name="Twitter / X"
          desc={loading ? "Checking connection…" : connected ? `Connected as @${status.username}` : "Post updates & track mentions"}
          connected={connected} />
        <Divider />
        {loading
          ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
          : connected
            ? <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
            : <ConnectBtn onClick={() => setShowModal(true)} />}
      </IntCard>
      {showModal && <TwitterCredModal onClose={() => setShowModal(false)} onSaved={info => setStatus({ configured: true, ...info })} />}
    </>
  );
}

// ─── Instagram card ───────────────────────────────────────────────────────
function InstagramCard() {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => { checkStatus(); }, []);
  async function checkStatus() {
    try { const d = await instagramAPI.status(); setStatus(d); }
    catch (e) { setStatus({ connected: false }); }
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const { authUrl } = await instagramAPI.connect();
      // Redirect user to Facebook/Instagram OAuth
      window.location.href = authUrl;
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to start Instagram OAuth');
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try { await instagramAPI.disconnect(); setStatus({ connected: false }); toast.success('Instagram disconnected.'); }
    catch (err) { toast.error(err.message || 'Failed'); }
    finally { setDisconnecting(false); }
  }

  const loading = status === null;
  const connected = status?.connected;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Instagram size={20} color="#E1306C" />} iconBg="#E1306C15" name="Instagram / Meta"
        desc={loading ? 'Checking connection…' : connected ? `Connected` : 'Publish posts & receive webhooks'}
        connected={connected} />
      <Divider />
      {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}><Loader2 size={16} color={mu} style={{ animation: 'spin 0.7s linear infinite' }} /></div>
        : connected ? <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
        : <ConnectBtn onClick={handleConnect} loading={connecting} />}
    </IntCard>
  );
}

// ─── LinkedIn card ───────────────────────────────────────────────────────
function LinkedInCard() {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => { checkStatus(); }, []);
  async function checkStatus() {
    try { const d = await linkedinAPI.status(); setStatus(d); if (d.connected) { const p = await linkedinAPI.pages(); setPages(p.pages || []); } }
    catch (e) { setStatus({ connected: false }); }
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const { authUrl } = await linkedinAPI.connect();
      window.location.href = authUrl;
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to start LinkedIn OAuth');
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try { await linkedinAPI.disconnect(); setStatus({ connected: false }); toast.success('LinkedIn disconnected.'); }
    catch (err) { toast.error(err.message || 'Failed'); }
    finally { setDisconnecting(false); }
  }

  const loading = status === null;
  const connected = status?.connected;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Linkedin size={20} color="#0A66C2" />} iconBg="#0A66C215" name="LinkedIn"
        desc={loading ? 'Checking connection…' : connected ? `Connected` : 'Share posts to LinkedIn'}
        connected={connected} />
      <Divider />
      {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}><Loader2 size={16} color={mu} style={{ animation: 'spin 0.7s linear infinite' }} /></div>
        : connected ? <><div style={{ fontSize: 12, color: '#667085', marginBottom: 10 }}>{pages.length ? `Page posting enabled: ${pages.map((p) => p.name || p.urn).join(', ')}` : 'Profile posting enabled. Reconnect with Page permissions to publish as a LinkedIn Page.'}</div><DisconnectBtn onClick={handleDisconnect} loading={disconnecting} /></>
        : <ConnectBtn onClick={handleConnect} loading={connecting} />}
    </IntCard>
  );
}

// ─── Gmail card ───────────────────────────────────────────────────────────────
function GmailCard() {
  const [status, setStatus] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  useEffect(() => {
    checkStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") { toast.success("Gmail connected!"); window.history.replaceState({}, "", window.location.pathname); checkStatus(); }
    else if (params.get("gmail") === "error") { toast.error("Gmail failed: " + (params.get("msg") || "unknown")); window.history.replaceState({}, "", window.location.pathname); }
  }, []);
  async function checkStatus() { try { const d = await gmailAPI.status(); setStatus(d); } catch { setStatus({ connected: false }); } }
  async function handleConnect() { setConnecting(true); try { const { url } = await gmailAPI.connect(); window.location.href = url; } catch (err) { toast.error(err.response?.data?.error || "Failed"); setConnecting(false); } }
  async function handleDisconnect() {
    setDisconnecting(true);
    try { await gmailAPI.disconnect(); setStatus({ connected: false }); toast.success("Gmail disconnected."); }
    catch (err) { toast.error(err.message); }
    finally { setDisconnecting(false); }
  }
  const loading = status === null;
  const connected = status?.connected;
  return (
    <>
      <IntCard connected={connected}>
        <CardHeader icon={<Mail size={20} color="#EA4335" />} iconBg="#EA433515" name="Gmail"
          desc={loading ? "Checking connection…" : connected ? status.email : "Send emails from your Gmail"}
          connected={connected} />
        <Divider />
        {loading
          ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
          : connected
            ? <div className="int-btn-group">
                <button type="button" className="int-btn ghost" onClick={() => setShowCompose(true)}><Mail size={13} />Compose</button>
                <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
              </div>
            : <ConnectBtn onClick={handleConnect} loading={connecting} />}
      </IntCard>
      {showCompose && <GmailComposeModal fromEmail={status?.email} onClose={() => setShowCompose(false)} />}
    </>
  );
}

// ─── Google Calendar card ─────────────────────────────────────────────────
function CalendarCard() {
  const [status, setStatus] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  useEffect(() => {
    checkStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendar") === "connected") { toast.success("Google Calendar connected!"); window.history.replaceState({}, "", window.location.pathname); checkStatus(); }
    else if (params.get("calendar") === "error") { toast.error("Calendar failed: " + (params.get("msg") || "unknown")); window.history.replaceState({}, "", window.location.pathname); }
  }, []);
  async function checkStatus() { try { const d = await calendarAPI.status(); setStatus(d); } catch { setStatus({ connected: false }); } }
  async function handleConnect() { setConnecting(true); try { const { url } = await calendarAPI.connect(); window.location.href = url; } catch (err) { toast.error(err.response?.data?.error || "Failed"); setConnecting(false); } }
  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      if (calendarAPI.disconnect) await calendarAPI.disconnect();
      setStatus({ connected: false });
      toast.success("Calendar disconnected.");
    } catch (err) { toast.error(err.message); }
    finally { setDisconnecting(false); }
  }
  const loading = status === null;
  const connected = status?.connected;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Mail size={20} color="#0ea5a6" />} iconBg="#0ea5a615" name="Google Calendar"
        desc={loading ? "Checking connection…" : connected ? status.email || 'connected' : "Manage your calendar and schedule events"}
        connected={connected} />
      <Divider />
      {loading
        ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
        : connected
          ? <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
          : <ConnectBtn onClick={handleConnect} loading={connecting} />}
    </IntCard>
  );
}

// ─── YouTube card ────────────────────────────────────────────────────────────
function YouTubeCard() {
  const [status, setStatus] = useState(null);
  const [problem, setProblem] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  useEffect(() => {
    checkStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("youtube") === "connected") { toast.success("YouTube connected!"); window.history.replaceState({}, "", window.location.pathname); }
    else if (params.get("youtube") === "error") { toast.error("YouTube failed: " + (params.get("msg") || "unknown")); window.history.replaceState({}, "", window.location.pathname); }
  }, []);
  async function checkStatus() {
    try {
      const d = await youtubeAPI.status(); setStatus(d);
      if (d.connected) { const t = await youtubeAPI.test().catch(() => null); setProblem(t && t.ok === false ? t.error : null); }
    } catch { setStatus({ connected: false }); }
  }
  async function handleConnect() { setConnecting(true); try { const { url } = await youtubeAPI.connect(); window.location.href = url; } catch (err) { toast.error(err.response?.data?.error || "Failed"); setConnecting(false); } }
  async function handleDisconnect() {
    setDisconnecting(true);
    try { await youtubeAPI.disconnect(); setStatus({ connected: false }); setProblem(null); toast.success("YouTube disconnected."); }
    catch (err) { toast.error(err.message); }
    finally { setDisconnecting(false); }
  }
  const loading = status === null;
  const connected = status?.connected;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Youtube size={20} color="#FF0000" />} iconBg="#FF00001F" name="YouTube"
        desc={loading ? "Checking connection…" : connected ? (status.channel || status.email || "connected") : "Search videos, summarise them and track channel stats"}
        connected={connected} />
      <Divider />
      {problem && <div style={{ fontSize: 12, color: "#B45309", padding: "0 0 8px", lineHeight: 1.4 }}>{problem}</div>}
      {loading
        ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
        : connected
          ? <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
          : <ConnectBtn onClick={handleConnect} loading={connecting} />}
    </IntCard>
  );
}

// ─── Request an integration ──────────────────────────────────────────────────
function IntegrationRequest() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  async function send() {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const r = await integrationRequestAPI.send(text.trim());
      toast.success(`Request sent from ${r.from}. We'll be in touch.`);
      setText(""); setOpen(false);
    } catch (err) { toast.error(err.response?.data?.error || "Could not send your request."); }
    finally { setSending(false); }
  }
  return (
    <div className="integration-request">
      <div className="integration-request-top">
        <div className="integration-request-copy">
          <div className="integration-request-icon"><Plug size={17} /></div>
          <div>
            <div className="integration-request-title">Need a custom integration?</div>
            <div className="integration-request-text">Tell us what you need. We're constantly adding new integrations.</div>
          </div>
        </div>
        {!open && <button type="button" onClick={() => setOpen(true)}>+ Request integration</button>}
      </div>
      {open && (
        <div className="integration-request-form">
          <textarea autoFocus value={text} maxLength={2000} rows={3} onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Notion — I'd like agents to read and update my project pages" />
          <div className="integration-request-actions">
            <button type="button" onClick={() => { setOpen(false); setText(""); }} disabled={sending}>Cancel</button>
            <button type="button" className="primary" onClick={send} disabled={!text.trim() || sending}>{sending ? "Sending…" : "Send"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Simple card (Slack, GitHub, etc.) ───────────────────────────────────────
function SimpleCard({ icon, iconBg, name, desc, badge }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  function handleConnect() {
    if (connected) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setConnected(true); }, 1200);
  }
  return (
    <IntCard connected={connected}>
      <CardHeader icon={icon} iconBg={iconBg} name={name} desc={desc} connected={connected}
        badges={badge ? [{ label: badge, bg: "var(--app-surface-3)", color: su }] : []} />
      <Divider />
      {connected
        ? <DisconnectBtn onClick={() => setConnected(false)} loading={false} />
        : <ConnectBtn onClick={handleConnect} loading={loading} />}
    </IntCard>
  );
}

// Lyra Atlas is intentionally configured as an MCP server instead of being
// embedded in AgentLyra's navigation. This keeps the engineering capability
// reusable by other hosts and makes its ownership explicit to the user.
function LyraAtlasCard() {
  const navigate = useNavigate();
  return (
    <IntCard>
      <CardHeader icon={<Brain size={20} color="#5B5CE2" />} iconBg="#5B5CE215" name="Lyra Atlas"
        desc="Connect local repository intelligence through MCP for coding agents." />
      <Divider />
      <ConnectBtn label="Configure MCP" onClick={() => navigate('/mcp-servers')} />
    </IntCard>
  );
}

// ─── GitHub card ──────────────────────────────────────────────────────────────
function GitHubCard() {
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  useEffect(() => { checkStatus(); }, []);
  async function checkStatus() {
    try { setStatus(await gitAPI.githubStatus()); }
    catch { setStatus({ connected: false }); }
  }
  async function handleSave() {
    if (!token.trim()) { toast.error("Paste your GitHub PAT first."); return; }
    setSaving(true);
    try {
      const d = await gitAPI.saveGithubToken(token.trim());
      setStatus({ connected: true, username: d.username });
      setToken("");
      toast.success(`GitHub connected as @${d.username}`);
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  }
  async function handleDisconnect() {
    setDisconnecting(true);
    try { await gitAPI.removeGithubToken(); setStatus({ connected: false }); toast.success("GitHub disconnected."); }
    catch (err) { toast.error(err.message); }
    finally { setDisconnecting(false); }
  }
  const loading = status === null;
  const connected = status?.connected;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Github size={20} color="var(--lyra-color-text)" />} iconBg="color-mix(in srgb, var(--lyra-color-text) 8%, transparent)" name="GitHub"
        desc={loading ? "Checking connection…" : connected ? `@${status.username}` : "Automate repos & pull requests"}
        connected={connected} />
      <Divider />
      {loading
        ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
        : connected
          ? <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
          : <>
            <ConnectBtn onClick={() => setFormOpen(true)} />
            {formOpen && <FormModal title="Connect GitHub" subtitle="Paste a personal access token. It is verified, then stored encrypted." onClose={() => setFormOpen(false)}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <InputField type={showToken ? "text" : "password"} value={token}
                onChange={e => setToken(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSave()}
                placeholder="ghp_..." accentColor="var(--lyra-color-text)"
                rightSlot={<button type="button" onClick={() => setShowToken(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: mu, padding: 0 }}>{showToken ? <EyeOff size={13} /> : <Eye size={13} />}</button>} />
              <button onClick={handleSave} disabled={saving || !token.trim()}
                style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: "none", cursor: token.trim() ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: token.trim() ? "var(--lyra-color-text)" : "var(--lyra-color-border)", color: token.trim() ? "var(--lyra-color-surface)" : mu, fontFamily: "inherit", opacity: token.trim() ? 1 : 0.7 }}>
                {saving ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Check size={13} />}
                {saving ? "Verifying…" : "Save token"}
              </button>
              <div style={{ fontSize: 11.5, color: mu, lineHeight: 1.4 }}>
                Generate a PAT at <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" style={{ color: blue }}>github.com/settings/tokens</a> with <code style={{ fontSize: 10.5, background: "var(--app-surface-3)", padding: "1px 5px", borderRadius: 4 }}>repo</code> scope
              </div>
            </div>
            </FormModal>}
          </>}
    </IntCard>
  );
}

// ─── GitLab card ──────────────────────────────────────────────────────────────
function GitLabCard() {
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  useEffect(() => { checkStatus(); }, []);
  async function checkStatus() {
    try { setStatus(await gitAPI.gitlabStatus()); }
    catch { setStatus({ connected: false }); }
  }
  async function handleSave() {
    if (!token.trim()) { toast.error("Paste your GitLab PAT first."); return; }
    setSaving(true);
    try {
      const d = await gitAPI.saveGitlabToken(token.trim());
      setStatus({ connected: true, username: d.username });
      setToken("");
      toast.success(`GitLab connected as @${d.username}`);
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  }
  async function handleDisconnect() {
    setDisconnecting(true);
    try { await gitAPI.removeGitlabToken(); setStatus({ connected: false }); toast.success("GitLab disconnected."); }
    catch (err) { toast.error(err.message); }
    finally { setDisconnecting(false); }
  }
  const loading = status === null;
  const connected = status?.connected;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<GitBranch size={20} color="#FC6D26" />} iconBg="#FC6D2615" name="GitLab"
        desc={loading ? "Checking connection…" : connected ? `@${status.username}` : "Automate repos & CI/CD pipelines"}
        connected={connected} />
      <Divider />
      {loading
        ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
        : connected
          ? <DisconnectBtn onClick={handleDisconnect} loading={disconnecting} />
          : <>
            <ConnectBtn onClick={() => setFormOpen(true)} />
            {formOpen && <FormModal title="Connect GitLab" subtitle="Paste a personal access token. It is verified, then stored encrypted." onClose={() => setFormOpen(false)}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <InputField type={showToken ? "text" : "password"} value={token}
                onChange={e => setToken(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSave()}
                placeholder="glpat-..." accentColor="#FC6D26"
                rightSlot={<button type="button" onClick={() => setShowToken(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: mu, padding: 0 }}>{showToken ? <EyeOff size={13} /> : <Eye size={13} />}</button>} />
              <button onClick={handleSave} disabled={saving || !token.trim()}
                style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: "none", cursor: token.trim() ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: token.trim() ? "#FC6D26" : "var(--lyra-color-border)", color: token.trim() ? "var(--lyra-color-surface)" : mu, fontFamily: "inherit", opacity: token.trim() ? 1 : 0.7 }}>
                {saving ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Check size={13} />}
                {saving ? "Verifying…" : "Save token"}
              </button>
              <div style={{ fontSize: 11.5, color: mu, lineHeight: 1.4 }}>
                Generate a PAT at <a href="https://gitlab.com/-/user_settings/personal_access_tokens" target="_blank" rel="noopener noreferrer" style={{ color: blue }}>gitlab.com</a> with <code style={{ fontSize: 10.5, background: "var(--app-surface-3)", padding: "1px 5px", borderRadius: 4 }}>read_api</code> scope
              </div>
            </div>
            </FormModal>}
          </>}
    </IntCard>
  );
}

// ─── WhatsApp card ────────────────────────────────────────────────────────────
// Linking is done by the user: the server issues a 6-digit code, the user texts it to Lyra's WhatsApp number, and the webhook
// links their phone. After that, alerts and agent keywords flow both ways.
function WhatsAppCard() {
  const [st, setSt] = useState(null);       // { linked, phone, name } from the server; null while loading
  const [code, setCode] = useState(null);   // { otp, waNumber, expiresIn } while the connect modal is open
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const authToken = () => localStorage.getItem("authToken") || localStorage.getItem("token");

  async function call(path, method = "GET") {
    const r = await fetch(`${API_ORIGIN}/whatsapp${path}`, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken()}` } });
    if (!(r.headers.get("content-type") || "").includes("json")) throw new Error("Could not reach the Lyra server. Check your connection and try again.");
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || d.message || "Something went wrong");
    return d;
  }
  const refresh = useCallback(async () => {
    try { const d = await call("/link/status"); setSt(d); return d; } catch { setSt((cur) => cur || { linked: false }); return null; }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // While the code is showing, watch for the user to text it in.
  useEffect(() => {
    if (!code) return undefined;
    const t = setInterval(async () => { const d = await refresh(); if (d?.linked) { toast.success("WhatsApp connected!"); setCode(null); } }, 2500);
    return () => clearInterval(t);
  }, [code, refresh]);

  async function start() {
    setBusy("start"); setErr("");
    try { setCode(await call("/link/generate", "POST")); } catch (e) { setErr(e.message); } finally { setBusy(""); }
  }
  async function disconnect() {
    setBusy("off");
    try { await call("/link", "DELETE"); setSt({ linked: false }); toast.success("WhatsApp disconnected"); } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  }

  const connected = !!st?.linked;
  return (
    <IntCard connected={connected}>
      <CardHeader icon={<MessageCircle size={20} color="#25D366" />} iconBg="#25D36615" name="WhatsApp"
        desc={connected ? `Alerts → +${st.phone}` : "Alerts & agent keywords on WhatsApp"}
        connected={connected} badges={[{ label: "HEARTBEAT", bg: "var(--lyra-color-success-bg)", color: "var(--lyra-color-success)" }]} />
      <Divider />
      {connected
        ? <DisconnectBtn onClick={disconnect} loading={busy === "off"} />
        : <>
            <ConnectBtn onClick={() => { setErr(""); start(); }} loading={busy === "start"} />
            {err && !code && <div style={{ fontSize: 12, color: "var(--lyra-color-danger)", marginTop: 8 }}>{err}</div>}
            {code && <FormModal title="Connect WhatsApp" subtitle="Link your phone with a one-time code" onClose={() => setCode(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", textAlign: "center" }}>
                <div style={{ fontSize: 13, color: su }}>Send this code in WhatsApp to <b>{code.waNumber}</b>. It expires in {code.expiresIn}.</div>
                <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: 6, fontFamily: "monospace", color: "#25D366" }}>{code.otp}</div>
                <div style={{ fontSize: 12, color: mu, display: "flex", alignItems: "center", gap: 6 }}><Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} />Waiting for your message…</div>
              </div>
            </FormModal>}
          </>}
    </IntCard>
  );
}

// ─── Telegram card ─────────────────────────────────────────────────────────
// Your own bot becomes a two-way control room: messages and results arrive in Telegram, and what you type there reaches Lyra
// (add tasks, ask what is due, run agents and workflows, approve emails). The token stays on the server; the browser only
// ever sees the bot's name.
const TG_ZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Los_Angeles", "Australia/Sydney", "UTC"];

function TelegramCard() {
  const [st, setSt] = useState(null);            // server status; null while loading
  const [modal, setModal] = useState(null);      // "connect" | "settings" | null
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [chatId, setChatId] = useState("");
  const [manual, setManual] = useState(false);
  const [own, setOwn] = useState(false);         // "use my own bot" (paste a token) instead of Lyra's bot
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const authToken = () => localStorage.getItem("authToken") || localStorage.getItem("token");

  async function call(path, method = "GET", body) {
    // Absolute URL: in production the web app and the API are on different hosts, and a relative path would be answered by the
    // web host's catch-all page (HTTP 200 with HTML), which looks like success but contains nothing.
    const r = await fetch(`${API_ORIGIN}/telegram${path}`, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken()}` }, body: body ? JSON.stringify(body) : undefined });
    if (!(r.headers.get("content-type") || "").includes("json")) throw new Error("Could not reach the Lyra server. Check your connection and try again.");
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || d.message || "Something went wrong");
    return d;
  }
  const refresh = useCallback(async () => {
    try { const d = await call("/status"); setSt(d); return d; } catch { setSt((cur) => cur || { connected: false, hasBot: false }); return null; }
  }, []);
  useEffect(() => {
    // Older versions kept the bot token in this browser. It lives on the server now, so remove the copy.
    try { localStorage.removeItem("tg_bot_token"); localStorage.removeItem("tg_chat_id"); } catch { /* nothing to remove */ }
    refresh();
  }, [refresh]);

  // While the "press Start" step is open, watch for the connection.
  const waiting = modal === "connect" && st?.hasBot && !st?.connected;
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(async () => { const d = await refresh(); if (d?.connected) { toast.success("Telegram connected!"); setModal(null); setToken(""); } }, 2000);
    return () => clearInterval(t);
  }, [waiting, refresh]);

  async function connect() {
    setBusy("connect"); setErr("");
    try {
      const d = await call("/connect", "POST", { botToken: token.trim(), ...(manual && chatId.trim() ? { chatId: chatId.trim() } : {}) });
      setSt(d);
      if (d.connected) { toast.success("Telegram connected! Check your bot for a hello."); setModal(null); setToken(""); }
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  }
  async function connectShared() {
    setBusy("shared"); setErr("");
    try {
      const d = await call("/connect-shared", "POST");
      setSt(d);
      if (d.connected) { toast.success("Telegram connected! Check Telegram for a hello."); setModal(null); }
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  }
  async function sendTest() {
    setBusy("test");
    try { await call("/test-message", "POST"); toast.success("Test message sent. Look in Telegram."); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  }
  async function save(patch) {
    setSt((cur) => ({ ...cur, ...patch }));   // instant; the server answer replaces it
    try { setSt(await call("/settings", "POST", patch)); } catch (e) { toast.error(e.message); refresh(); }
  }
  async function disconnect() {
    if (!window.confirm("Disconnect Telegram? Messages and notifications will stop until you connect again.")) return;
    setBusy("remove");
    try { await call("/config", "DELETE"); setSt({ connected: false, hasBot: false }); toast.success("Telegram disconnected."); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  }

  const connected = !!st?.connected;
  const zones = st?.timezone && !TG_ZONES.includes(st.timezone) ? [st.timezone, ...TG_ZONES] : TG_ZONES;
  const toggle = (on, onClick, label) => (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onClick}
      style={{ width: 36, height: 20, borderRadius: 99, border: "none", cursor: "pointer", padding: 2, background: on ? "var(--lyra-color-success)" : "var(--app-border-strong)", display: "flex", justifyContent: on ? "flex-end" : "flex-start", flexShrink: 0 }}>
      <span style={{ width: 16, height: 16, borderRadius: 99, background: "var(--lyra-color-surface)" }} />
    </button>
  );
  const step = (n, title, body) => (
    <div style={{ display: "flex", gap: 10 }}>
      <span style={{ width: 22, height: 22, borderRadius: 99, background: "var(--app-primary-soft)", color: "var(--app-primary-deep)", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{n}</span>
      <div style={{ minWidth: 0, flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: tx }}>{title}</div>{body}</div>
    </div>
  );

  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Send size={20} color="#229ED9" />} iconBg="#229ED91F" name="Telegram"
        desc={st === null ? "Checking…" : connected ? `@${st.botUsername || "your bot"} · two-way` : st?.hasBot ? "Waiting for you to press Start in your bot" : "Chat with Lyra, get alerts and results, approve actions"}
        connected={connected} badges={[{ label: "TWO-WAY", bg: "var(--lyra-color-success-bg)", color: "var(--lyra-color-success)" }]} />
      <Divider />
      {st === null ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
      ) : connected ? (
        <>
          <a href={`https://t.me/${st.botUsername}`} target="_blank" rel="noopener noreferrer" className="int-btn ghost" title="Open your bot in Telegram" style={{ textDecoration: "none" }}>Open bot</a>
          <button type="button" className="int-btn ghost" disabled={busy === "test"} onClick={sendTest} title="Send a test message to your Telegram">{busy === "test" ? "Sending…" : "Test"}</button>
          <button type="button" className="int-btn ghost" onClick={() => setModal("settings")} title="Daily digest and task reminders">Digest</button>
          <DisconnectBtn onClick={disconnect} loading={busy === "remove"} />
        </>
      ) : (
        <ConnectBtn onClick={() => { setErr(""); setOwn(false); setModal("connect"); }} label={st?.hasBot ? "Finish connecting" : "Connect"} />
      )}

      {modal === "connect" && (
        <FormModal title="Connect Telegram" subtitle="A two-way control room for Lyra: chat, alerts and approvals." onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {!st?.hasBot && !own && (
              <>
                <div style={{ border: `1.5px solid var(--app-primary-soft-2)`, background: "var(--app-primary-soft)", borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: tx }}>Use Lyra's bot <span style={{ fontSize: 11, fontWeight: 700, color: "var(--lyra-color-success)", marginLeft: 6 }}>RECOMMENDED</span></div>
                  <div style={{ fontSize: 12.5, color: su, lineHeight: 1.5, margin: "4px 0 10px" }}>No setup. Tap once, press Start in Telegram, and you are connected.</div>
                  {err && <div role="alert" style={{ fontSize: 12.5, color: "var(--lyra-color-danger)", marginBottom: 8 }}>{err}</div>}
                  <button onClick={connectShared} disabled={busy === "shared"} style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#229ED9", color: "#fff" }}>
                    {busy === "shared" ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Send size={13} />}Use Lyra's bot
                  </button>
                </div>
                <button type="button" onClick={() => { setErr(""); setOwn(true); }} style={{ background: "none", border: "none", color: blue, fontSize: 12.5, cursor: "pointer", textAlign: "left", padding: 0 }}>
                  Prefer your own bot? Your own name, picture and privacy →
                </button>
              </>
            )}
            {!st?.hasBot && own && (
              <>
                {step(1, "Create your bot", (
                  <div style={{ fontSize: 12.5, color: su, lineHeight: 1.5 }}>Open <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer" style={{ color: blue }}>@BotFather</a>, send <b>/newbot</b>, pick a name, and copy the token it gives you.
                    <div style={{ marginTop: 8 }}>
                      <InputField type={showToken ? "text" : "password"} value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste the bot token (123456789:ABC…)" accentColor="#229ED9"
                        onKeyDown={(e) => e.key === "Enter" && token.trim() && !busy && connect()}
                        rightSlot={<button type="button" onClick={() => setShowToken((v) => !v)} aria-label={showToken ? "Hide token" : "Show token"} style={{ background: "none", border: "none", cursor: "pointer", color: mu, padding: 0 }}>{showToken ? <EyeOff size={13} /> : <Eye size={13} />}</button>} />
                    </div>
                  </div>
                ))}
                {manual && <InputField type="text" value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="Your chat ID (optional, from @userinfobot)" accentColor="#229ED9" />}
                {err && <div role="alert" style={{ fontSize: 12.5, color: "var(--lyra-color-danger)" }}>{err}</div>}
                <button onClick={connect} disabled={busy === "connect" || !token.trim()} style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: "none", cursor: token.trim() ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: token.trim() ? "#229ED9" : "var(--app-border-strong)", color: "#fff" }}>
                  {busy === "connect" ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Check size={13} />}Verify bot
                </button>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button type="button" onClick={() => setManual((m) => !m)} style={{ background: "none", border: "none", color: mu, fontSize: 11.5, cursor: "pointer", padding: 0 }}>{manual ? "Skip the chat ID" : "I already know my chat ID"}</button>
                  <button type="button" onClick={() => { setErr(""); setOwn(false); }} style={{ background: "none", border: "none", color: mu, fontSize: 11.5, cursor: "pointer", padding: 0 }}>← Use Lyra's bot instead</button>
                </div>
              </>
            )}
            {st?.hasBot && !st?.connected && step(2, "Press Start in your bot", (
              <div style={{ fontSize: 12.5, color: su, lineHeight: 1.5 }}>
                {st.link ? <>Open <b>@{st.botUsername}</b> and press <b>Start</b>. I will pick up your chat automatically.{st.sharedBot ? " This is Lyra's own bot, so there is nothing to set up." : ""}</> : <>Open <b>@{st.botUsername}</b> in Telegram, press <b>Start</b>, then verify again.</>}
                <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {st.link && <a href={st.link} target="_blank" rel="noopener noreferrer" className="int-btn primary" style={{ textDecoration: "none" }}>Open @{st.botUsername}</a>}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: mu }}><Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> Waiting for you…</span>
                </div>
                {st.link && (
                  <div style={{ marginTop: 10, fontSize: 12, color: su, lineHeight: 1.5 }}>
                    Link not opening the chat, or you already started this bot? Send this code to <b>@{st.botUsername}</b> instead:
                    <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
                      <code style={{ padding: "4px 9px", borderRadius: 7, background: "var(--app-surface-3)", color: tx, fontSize: 13, letterSpacing: ".06em" }}>{(st.link.split("start=")[1] || "").trim()}</code>
                      <button type="button" className="int-btn ghost" style={{ height: 28, padding: "0 10px", fontSize: 12 }} onClick={() => { navigator.clipboard?.writeText((st.link.split("start=")[1] || "").trim()); toast.success("Code copied"); }}>Copy</button>
                    </div>
                  </div>
                )}
                {!st.link && <button onClick={connect} className="int-btn" style={{ marginTop: 8 }}>Get a new link</button>}
              </div>
            ))}
          </div>
        </FormModal>
      )}

      {modal === "settings" && (
        <FormModal title="Digest & reminders" subtitle="Things Lyra sends to your Telegram on its own." onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: tx }}>Daily digest</div><div style={{ fontSize: 12, color: su }}>Due today, overdue and scheduled runs, every morning.</div></div>
              {toggle(!!st.digestEnabled, () => save({ digestEnabled: !st.digestEnabled }), "Daily digest")}
            </div>
            {st.digestEnabled && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input type="time" value={st.digestTime || "08:00"} onChange={(e) => e.target.value && save({ digestTime: e.target.value })} aria-label="Digest time" style={{ padding: "8px 10px", borderRadius: 9, border: `1.5px solid ${bord}`, background: "var(--lyra-color-surface-2)", color: tx, fontSize: 13, fontFamily: "inherit" }} />
                <select value={st.timezone || "Asia/Kolkata"} onChange={(e) => save({ timezone: e.target.value })} aria-label="Time zone" style={{ flex: 1, minWidth: 140, padding: "8px 10px", borderRadius: 9, border: `1.5px solid ${bord}`, background: "var(--lyra-color-surface-2)", color: tx, fontSize: 13, fontFamily: "inherit" }}>
                  {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: tx }}>Task reminders</div><div style={{ fontSize: 12, color: su }}>A nudge about 15 minutes before a task with a due time, with a Done button.</div></div>
              {toggle(!!st.dueReminders, () => save({ dueReminders: !st.dueReminders }), "Task reminders")}
            </div>
            <div style={{ fontSize: 12, color: su, lineHeight: 1.6, borderTop: `1px solid ${bord}`, paddingTop: 12 }}>
              <b style={{ color: tx }}>Try in Telegram:</b> “add a task: renew passport by Friday 5pm” · <code>/tasks</code> · <code>/today</code> · <code>/run 1 summarise my inbox</code> · <code>/runflow 1</code> · “email Priya I am running late” (you approve before it is sent).
            </div>
          </div>
        </FormModal>
      )}
    </IntCard>
  );
}

// ─── Webhook card ─────────────────────────────────────────────────────────────
function WebhookCard() {
  const [formOpen, setFormOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  function handleSave() {
    if (!url.trim() || !url.startsWith("http")) { toast.error("Enter a valid webhook URL."); return; }
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); setFormOpen(false); toast.success("Webhook saved!"); }, 800);
  }
  return (
    <IntCard connected={saved}>
      <CardHeader icon={<Webhook size={20} color="var(--lyra-color-primary)" />} iconBg="color-mix(in srgb, var(--lyra-color-primary) 8%, transparent)" name="Outbound Webhook"
        desc="Push agent events to Engram or any external endpoint"
        connected={saved} badges={[{ label: "ENGRAM", bg: "var(--app-primary-soft)", color: "var(--lyra-color-primary)" }]} />
      <Divider />
      <ConnectBtn onClick={() => setFormOpen(true)} label={saved ? "Edit webhook" : "Connect"} />
      {formOpen && <FormModal title="Outbound webhook" subtitle="Push agent events to Engram or any external endpoint." onClose={() => setFormOpen(false)}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <InputField type="url" value={url} onChange={e => { setUrl(e.target.value); setSaved(false); }} placeholder="https://your-engram.com/webhook" accentColor="var(--lyra-color-primary)" onKeyDown={e => e.key === "Enter" && handleSave()} />
        <button onClick={handleSave} disabled={saving || !url.trim()} style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: "none", cursor: url.trim() ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: url.trim() ? "var(--lyra-color-primary)" : "var(--lyra-color-border)", color: url.trim() ? "var(--lyra-color-surface)" : mu, fontFamily: "inherit", opacity: url.trim() ? 1 : 0.7 }}>
          {saving ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : saved ? <><CheckCircle2 size={13} />Active</> : <><Zap size={13} />Save webhook</>}
        </button>
      </div>
      </FormModal>}
    </IntCard>
  );
}

// ─── Engram API helpers ───────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL ?? "";
function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}
const engramAPI = {
  status:     async () => { const r = await fetch(`${API_BASE}/api/engram/status`, { headers: authHeaders() }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  enable:     async () => { const r = await fetch(`${API_BASE}/api/engram/enable`, { method: "POST", headers: authHeaders() }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  connectKey: async (k) => { const r = await fetch(`${API_BASE}/api/engram/connect-key`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ apiKey: k }) }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  disable:    async () => { const r = await fetch(`${API_BASE}/api/engram/disable`, { method: "DELETE", headers: authHeaders() }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  magicLink:  async () => { const r = await fetch(`${API_BASE}/api/engram/magic-link`, { headers: authHeaders() }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
  toggleChat: async (en) => { const r = await fetch(`${API_BASE}/api/engram/chat`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ enabled: en }) }); const d = await r.json(); if (!r.ok) throw new Error(d.message ?? "Failed"); return d; },
};

// ─── Engram card ──────────────────────────────────────────────────────────────
function EngramCard() {
  const { engramChatEnabled, setEngramChatEnabled } = useAI();
  const [connected, setConnected] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [togglingChat, setTogglingChat] = useState(false);
  const [openingDashboard, setOpeningDashboard] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualKey, setManualKey] = useState("");
  const [showManualKey, setShowManualKey] = useState(false);
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    engramAPI.status()
      .then(d => { setConnected(d.connected); if (d.engramChatEnabled !== undefined) setEngramChatEnabled(d.engramChatEnabled); })
      .catch(() => setConnected(false))
      .finally(() => setLoadingStatus(false));
  }, []);

  async function handleEnable() {
    setEnabling(true);
    try {
      const d = await engramAPI.enable();
      if (d.manualKeyRequired) { setShowManual(true); toast("Paste your Engram API key to connect.", { icon: "🔑" }); }
      else { setConnected(true); toast.success("Memory enabled! Your agents now have persistent context."); }
    } catch (err) {
      if (err.message?.toLowerCase().includes("manual")) { setShowManual(true); toast("Paste your Engram API key.", { icon: "🔑" }); }
      else toast.error(err.message);
    } finally { setEnabling(false); }
  }
  async function handleConnectKey() {
    if (!manualKey.trim()) { toast.error("Paste your Engram API key first."); return; }
    setSavingManual(true);
    try { await engramAPI.connectKey(manualKey.trim()); setConnected(true); setManualKey(""); setShowManual(false); toast.success("Engram key connected!"); }
    catch (err) { toast.error(err.message); }
    finally { setSavingManual(false); }
  }
  async function handleRemove() {
    setRemoving(true);
    try { await engramAPI.disable(); setConnected(false); setEngramChatEnabled(false); toast.success("Engram memory disconnected."); }
    catch (err) { toast.error(err.message); }
    finally { setRemoving(false); }
  }
  async function handleOpenDashboard() {
    setOpeningDashboard(true);
    try { const { url } = await engramAPI.magicLink(); window.open(url, "_blank", "noopener,noreferrer"); }
    catch { toast.error("Could not open Engram dashboard."); }
    finally { setOpeningDashboard(false); }
  }
  async function handleToggleChat() {
    setTogglingChat(true);
    try { const { engramChatEnabled: updated } = await engramAPI.toggleChat(!engramChatEnabled); setEngramChatEnabled(updated); }
    catch (err) { toast.error(err.message); }
    finally { setTogglingChat(false); }
  }

  return (
    <IntCard connected={connected}>
      <CardHeader icon={<Brain size={20} color="var(--lyra-color-primary)" />} iconBg={connected ? "var(--app-primary-soft)" : "color-mix(in srgb, var(--lyra-color-primary) 8%, transparent)"}
        name="Engram Memory" desc={loadingStatus ? "Checking…" : connected ? "Personal memory space · agents inject your past context automatically" : "Persistent vector memory for all your agents"}
        connected={connected} badges={[{ label: "QDRANT", bg: "var(--app-primary-soft)", color: "var(--lyra-color-primary)" }]} />

      <Divider />

      {loadingStatus
        ? <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><Loader2 size={16} color={mu} style={{ animation: "spin 0.7s linear infinite" }} /></div>
        : connected
          ? <>
              {/* Chat memory toggle */}
              <div className="int-actions-extra" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: engramChatEnabled ? "var(--app-primary-soft)" : "var(--lyra-color-surface-2)", border: `1px solid ${engramChatEnabled ? "var(--app-primary-soft-2)" : bord}`, borderRadius: 9, transition: "all 0.2s" }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: engramChatEnabled ? "var(--lyra-color-primary)" : tx }}>Use memory in chats</div>
                  <div style={{ fontSize: 11.5, color: mu, marginTop: 2 }}>{engramChatEnabled ? "✓ Sessions inject & save memories" : "Off — only agents use Engram"}</div>
                </div>
                <button onClick={handleToggleChat} disabled={togglingChat} style={{ position: "relative", width: 38, height: 20, borderRadius: 999, border: "none", cursor: togglingChat ? "not-allowed" : "pointer", padding: 0, outline: "none", flexShrink: 0, background: engramChatEnabled ? "var(--lyra-color-primary)" : "var(--app-border-strong)", transition: "background 0.2s", opacity: togglingChat ? 0.6 : 1 }}>
                  <span style={{ position: "absolute", top: 3, left: engramChatEnabled ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "var(--lyra-color-surface)", boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left 0.2s", display: "block" }} />
                </button>
              </div>
              {/* Actions */}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleOpenDashboard} disabled={openingDashboard} style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: "1.5px solid var(--app-primary-soft-2)", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "var(--app-primary-soft)", color: "var(--lyra-color-primary)", fontFamily: "inherit", opacity: openingDashboard ? 0.6 : 1 }}>
                  {openingDashboard ? <Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} /> : <ExternalLink size={12} />}Manage
                </button>
                <button onClick={handleRemove} disabled={removing} style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: "1.5px solid var(--app-danger-border)", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "var(--lyra-color-danger-bg)", color: "var(--lyra-color-danger)", fontFamily: "inherit" }}>
                  {removing ? <Loader2 size={12} style={{ animation: "spin 0.7s linear infinite" }} /> : <LogOut size={13} />}Disconnect
                </button>
              </div>
            </>
          : <>
              <ConnectBtn onClick={handleEnable} loading={enabling} label="Enable" tone="accent" />
              {/* Manual key */}
              <div className="int-actions-extra">
                <button onClick={() => setShowManual(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: su, display: "flex", alignItems: "center", gap: 4, padding: 0, fontFamily: "inherit" }}>
                  <ChevronDown size={13} style={{ transition: "transform 0.2s", transform: showManual ? "rotate(180deg)" : "rotate(0deg)" }} />
                  Already have an Engram key?
                </button>
                {showManual && (
                  <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <InputField type={showManualKey ? "text" : "password"} value={manualKey} onChange={e => setManualKey(e.target.value)} onKeyDown={e => e.key === "Enter" && handleConnectKey()} placeholder="Paste Engram API key (egm_…)" accentColor="var(--lyra-color-primary)"
                        rightSlot={<button type="button" onClick={() => setShowManualKey(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: mu, padding: 0 }}>{showManualKey ? <EyeOff size={13} /> : <Eye size={13} />}</button>} />
                    </div>
                    <button onClick={handleConnectKey} disabled={savingManual || !manualKey.trim()} style={{ padding: "9px 14px", borderRadius: 9, border: "none", cursor: manualKey.trim() ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13, background: manualKey.trim() ? "var(--lyra-color-primary)" : "var(--lyra-color-border)", color: manualKey.trim() ? "var(--lyra-color-surface)" : mu, display: "flex", alignItems: "center", gap: 5, fontFamily: "inherit" }}>
                      {savingManual ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Check size={13} />}
                    </button>
                  </div>
                )}
              </div>
            </>}
    </IntCard>
  );
}

// ─── Data stores: MongoDB, AWS S3, Cloudflare R2 ──────────────────────────────
const DATA_PROVIDERS = {
  mongodb: {
    name: "MongoDB", desc: "Connect your MongoDB databases.", color: "#13AA52", Icon: Database,
    subtitle: "The connection is tested, then stored encrypted. The database must be reachable from the internet.",
    fields: [
      { key: "uri", label: "Connection string", placeholder: "mongodb+srv://user:password@cluster.mongodb.net", secret: true },
      { key: "database", label: "Database (optional)", placeholder: "my_app" },
    ],
    describe: (c) => [c.host, c.database].filter(Boolean).join(" · ") || "Connected",
  },
  s3: {
    name: "AWS S3", desc: "Store and read files in S3 buckets.", color: "#E25444", Icon: HardDrive,
    subtitle: "Use an access key that can read the bucket. It is tested, then stored encrypted.",
    fields: [
      { key: "region", label: "Region", placeholder: "us-east-1" },
      { key: "bucket", label: "Bucket", placeholder: "my-bucket" },
      { key: "accessKeyId", label: "Access key ID", placeholder: "AKIA…" },
      { key: "secretAccessKey", label: "Secret access key", placeholder: "", secret: true },
    ],
    describe: (c) => [c.bucket, c.region].filter(Boolean).join(" · ") || "Connected",
  },
  r2: {
    name: "Cloudflare R2", desc: "Object storage with zero egress fees.", color: "#F38020", Icon: Cloud,
    subtitle: "Create an R2 API token with read access. It is tested, then stored encrypted.",
    fields: [
      { key: "accountId", label: "Account ID", placeholder: "32-character Cloudflare account ID" },
      { key: "bucket", label: "Bucket", placeholder: "my-bucket" },
      { key: "accessKeyId", label: "Access key ID", placeholder: "" },
      { key: "secretAccessKey", label: "Secret access key", placeholder: "", secret: true },
    ],
    describe: (c) => c.bucket || "Connected",
  },
};

function DataConnectionCard({ provider }) {
  const p = DATA_PROVIDERS[provider];
  const { dataConnections, refresh } = useContext(IntegrationCtx);
  const conn = dataConnections.find(c => c.provider === provider);
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  const ready = p.fields.every(f => /optional/i.test(f.label) || (vals[f.key] || "").trim());

  function close() { setOpen(false); setVals({}); setError(""); }
  async function handleSave() {
    setSaving(true); setError("");
    try {
      await dataConnectionsAPI.save(provider, vals);
      toast.success(`${p.name} connected`);
      close();
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Could not connect.");
    } finally { setSaving(false); }
  }
  async function handleDisconnect() {
    setRemoving(true);
    try { await dataConnectionsAPI.remove(provider); toast.success(`${p.name} disconnected.`); refresh(); }
    catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setRemoving(false); }
  }

  return (
    <IntCard connected={!!conn}>
      <CardHeader icon={<p.Icon size={20} color={p.color} />} iconBg={`${p.color}1F`} name={p.name}
        desc={conn ? p.describe(conn.config || {}) : p.desc} connected={!!conn} />
      <Divider />
      {conn
        ? <DisconnectBtn onClick={handleDisconnect} loading={removing} />
        : <ConnectBtn onClick={() => setOpen(true)} />}
      {open && (
        <FormModal title={`Connect ${p.name}`} subtitle={p.subtitle} onClose={close}>
          <form className="int-form" onSubmit={(e) => { e.preventDefault(); if (ready && !saving) handleSave(); }} autoComplete="off">
            {p.fields.map(f => (
              <label key={f.key} className="int-field">
                <span>{f.label}</span>
                <InputField type={f.secret ? "password" : "text"} value={vals[f.key] || ""} placeholder={f.placeholder}
                  onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))} />
              </label>
            ))}
            {error && <div className="int-error" role="alert">{error}</div>}
            <button type="submit" className="int-btn primary" disabled={!ready || saving}>
              {saving ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Check size={13} />}
              {saving ? "Testing connection…" : "Test & connect"}
            </button>
          </form>
        </FormModal>
      )}
    </IntCard>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
// category: Communication | Productivity | Development | Data & Storage | Design | Other
const INTEGRATION_DEFS = [
  { id: "gmail", name: "Gmail", category: "Communication", popular: true, docs: "https://developers.google.com/gmail/api", description: "Send & receive emails", render: () => <GmailCard /> },
  { id: "calendar", name: "Google Calendar", category: "Productivity", docs: "https://developers.google.com/calendar/api", description: "Manage events & schedules", render: () => <CalendarCard /> },
  { id: "slack", name: "Slack", category: "Communication", popular: true, docs: "https://api.slack.com/docs", description: "Send messages & manage channels", render: () => <SimpleCard icon={<Slack size={20} color="#E01E5A" />} iconBg="#E01E5A1F" name="Slack" desc="Send messages, manage channels and get alerts." /> },
  { id: "whatsapp", name: "WhatsApp", category: "Communication", popular: true, docs: "https://developers.facebook.com/docs/whatsapp", description: "Send notifications & alerts", render: () => <WhatsAppCard /> },
  { id: "telegram", name: "Telegram", category: "Communication", docs: "https://core.telegram.org/bots", description: "Agent alerts & heartbeats", render: () => <TelegramCard /> },
  { id: "github", name: "GitHub", category: "Development", popular: true, docs: "https://docs.github.com/rest", description: "Automate repos & pull requests", render: () => <GitHubCard /> },
  { id: "gitlab", name: "GitLab", category: "Development", docs: "https://docs.gitlab.com/ee/api/", description: "Automate repos & CI/CD pipelines", render: () => <GitLabCard /> },
  { id: "lyra-atlas", name: "Lyra Atlas", category: "Development", description: "Connect local repository intelligence through MCP", render: () => <LyraAtlasCard /> },
  { id: "mongodb", name: "MongoDB", category: "Data & Storage", docs: "https://www.mongodb.com/docs/manual/reference/connection-string/", description: "Connect your MongoDB databases", render: () => <DataConnectionCard provider="mongodb" /> },
  { id: "s3", name: "AWS S3", category: "Data & Storage", docs: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/", description: "Store and read files in S3 buckets", render: () => <DataConnectionCard provider="s3" /> },
  { id: "r2", name: "Cloudflare R2", category: "Data & Storage", docs: "https://developers.cloudflare.com/r2/", description: "Object storage with zero egress fees", render: () => <DataConnectionCard provider="r2" /> },
  { id: "figma", name: "Figma", category: "Design", docs: "https://www.figma.com/developers/api", description: "Sync designs & component updates", render: () => <SimpleCard icon={<Figma size={20} color="#F24E1E" />} iconBg="#F24E1E1F" name="Figma" desc="Access designs and component libraries." /> },
  { id: "trello", name: "Trello", category: "Productivity", docs: "https://developer.atlassian.com/cloud/trello/", description: "Manage boards, cards & lists", render: () => <SimpleCard icon={<Trello size={20} color="#2684FF" />} iconBg="#2684FF1F" name="Trello" desc="Manage boards, cards and lists." /> },
  { id: "twitter", name: "Twitter / X", category: "Communication", docs: "https://developer.x.com/en/docs", description: "Post updates & track mentions", render: () => <TwitterCard /> },
  { id: "instagram", name: "Instagram / Meta", category: "Communication", docs: "https://developers.facebook.com/docs/instagram-platform", description: "Publish posts & receive webhooks", render: () => <InstagramCard /> },
  { id: "linkedin", name: "LinkedIn", category: "Communication", docs: "https://learn.microsoft.com/linkedin/", description: "Share posts to LinkedIn", render: () => <LinkedInCard /> },
  { id: "youtube", name: "YouTube", category: "Communication", docs: "https://developers.google.com/youtube/v3", description: "Search videos, summarise them & track channel stats", render: () => <YouTubeCard /> },
  { id: "chrome", name: "Chrome", category: "Other", docs: "https://developer.chrome.com/docs/extensions", description: "Capture browsing context for Engram", render: () => <SimpleCard icon={<Chrome size={20} color="#FBBC05" />} iconBg="#FBBC051F" name="Chrome" desc="Capture browsing context for Engram." /> },
  { id: "webhook", name: "Outbound Webhook", category: "Development", description: "Push agent events to an external endpoint", render: () => <WebhookCard /> },
  { id: "engram", name: "Engram Memory", category: "Other", description: "Persistent memory for your agents", render: () => <EngramCard /> },
];

const FILTERS = ["All", "Connected", "Popular", "Communication", "Productivity", "Development", "Data & Storage", "Design", "Other"];

const CONNECTED_CHECKS = {
  gmail: () => isChannelConnected("email"),
  calendar: () => calendarAPI.status().then(d => !!d?.connected),
  youtube: () => youtubeAPI.status().then(d => !!d?.connected),
  twitter: () => twitterAPI.credentialsStatus().then(d => !!d?.configured),
  instagram: () => instagramAPI.status().then(d => !!d?.connected),
  linkedin: () => linkedinAPI.status().then(d => !!d?.connected),
  github: () => gitAPI.githubStatus().then(d => !!d?.connected),
  gitlab: () => gitAPI.gitlabStatus().then(d => !!d?.connected),
  engram: () => engramAPI.status().then(d => !!d?.connected),
  telegram: () => isChannelConnected("telegram"),
};

const HERO_TILES = [
  { Icon: Mail, color: "#EA4335", style: { top: 20, left: 40, transform: "rotate(-8deg)" } },
  { Icon: Slack, color: "#E01E5A", style: { top: 8, right: 30, transform: "rotate(8deg)" } },
  { Icon: Github, color: "#24292F", style: { top: 100, left: 0, transform: "rotate(-4deg)" } },
  { Icon: HardDrive, color: "#1FA463", style: { bottom: 0, left: 120, transform: "rotate(10deg)" } },
  { Icon: Figma, color: "#F24E1E", style: { bottom: 10, right: 0, transform: "rotate(-6deg)" } },
];

export default function IntegrationsPage() {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("default");
  const [connectedIds, setConnectedIds] = useState([]);
  const [dataConnections, setDataConnections] = useState([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    const entries = Object.entries(CONNECTED_CHECKS);
    const [results, data] = await Promise.all([
      Promise.all(entries.map(async ([id, check]) => {
        try { return [id, await check()]; } catch { return [id, false]; }
      })),
      dataConnectionsAPI.list().then(d => d?.connections || []).catch(() => []),
    ]);
    if (!alive.current) return;
    setDataConnections(data);
    setConnectedIds([...results.filter(([, connected]) => connected).map(([id]) => id), ...data.map(c => c.provider)]);
    setConnectionsLoaded(true);
  }, []);

  useEffect(() => {
    alive.current = true;
    refresh();
    return () => { alive.current = false; };
  }, [refresh]);

  // `connect` is an integration identifier, not free-form search text.  Clear
  // stale or malformed values (such as an email address) whenever navigation
  // changes, so a deep link can never blank the integrations catalog.
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("connect") || "";
    const valid = INTEGRATION_DEFS.some(item => item.id === requested);
    setQuery(valid ? requested : "");
  }, [location.search, location.hash]);

  // Deep-link support (e.g. /integrations#calendar, from RunDrawer's
  // "Connect X" prompt) — scroll to and briefly highlight the matching card.
  useEffect(() => {
    const hash = (location && location.hash) ? location.hash.replace(/^#/, '') : '';
    if (!hash) return;
    const el = document.querySelector(`[data-integration-id="${hash}"]`);
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); }
    el.classList.add('integration-highlight');
    const t = setTimeout(() => el.classList.remove('integration-highlight'), 2000);
    return () => clearTimeout(t);
  }, [location]);

  const normalized = query.trim().toLowerCase();
  const matches = item => {
    const text = `${item.name} ${item.description} ${item.category}`.toLowerCase();
    const searchMatch = !normalized || text.includes(normalized);
    const categoryMatch = category === "All"
      || (category === "Connected" ? connectedIds.includes(item.id)
        : category === "Popular" ? !!item.popular
          : item.category === category);
    return searchMatch && categoryMatch;
  };
  const sorted = list => (sort === "default" ? list : [...list].sort((a, b) => (sort === "az" ? 1 : -1) * a.name.localeCompare(b.name)));

  const filtered = INTEGRATION_DEFS.filter(matches);
  const connectedItems = sorted(filtered.filter(item => connectedIds.includes(item.id)));
  const availableItems = sorted(filtered.filter(item => !connectedIds.includes(item.id)));
  const filtering = category !== "All" || !!normalized;

  const renderGrid = (items, variant = "available") => (
    <div className={`integration-grid ${variant}`}>
      {items.map(item => (
        <div className="integration-grid-item" data-integration-id={item.id} key={item.id}>
          <IntegrationCtx.Provider value={{ category: item.category, popular: !!item.popular, docsUrl: item.docs || "", dataConnections, refresh }}>
            {item.render()}
          </IntegrationCtx.Provider>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <style>{PAGE_CSS}</style>

      <div className="integrations-page">
        <div className="integrations-container">
          <header className="int-hero">
            <div className="int-hero-copy">
              <span className="int-hero-tag">Integrations</span>
              <h1 className="integrations-title">Connect your tools</h1>
              <p className="integrations-subtitle">Give your agents access to the tools, apps and services you use. Connect once and start using them in chat and workflows.</p>
            </div>
            <div className="int-hero-art" aria-hidden="true">
              {HERO_TILES.map(({ Icon, color, style }, i) => (
                <span key={i} className="int-hero-tile" style={style}><Icon size={26} color={color} /></span>
              ))}
              <span className="int-hero-tile center"><Plug size={34} color="#fff" /></span>
            </div>
          </header>

          <div className="integration-toolbar">
            <div className="int-chips" role="tablist" aria-label="Filter integrations">
              {FILTERS.map(f => (
                <button key={f} type="button" role="tab" aria-selected={category === f} className={`int-chip${category === f ? " active" : ""}`} onClick={() => setCategory(f)}>
                  {f === "Connected" && <i className="int-dot" />}
                  {f}
                  {f === "All" && <b>{INTEGRATION_DEFS.length}</b>}
                  {f === "Connected" && <b>{connectedIds.length}</b>}
                </button>
              ))}
            </div>
            <div className="int-tools">
              <div className="integration-search">
                <Search size={15} />
                <input type="search" name="integration-search" autoComplete="off" data-lpignore="true" data-1p-ignore="true" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search integrations…" aria-label="Search integrations" />
              </div>
              <label className="integration-sort">
                <ArrowUpDown size={14} />
                <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort integrations">
                  <option value="default">Sort</option>
                  <option value="az">Name A–Z</option>
                  <option value="za">Name Z–A</option>
                </select>
              </label>
            </div>
          </div>

          {!connectionsLoaded ? (
            <div className="integration-empty"><Loader2 size={18} style={{ animation: "spin 0.7s linear infinite", verticalAlign: "middle", marginRight: 8 }} />Loading integrations…</div>
          ) : <>
          {category !== "Connected" && connectedItems.length > 0 && (
            <section className="integration-section">
              <h2 className="integration-section-title">Connected <span>({connectedItems.length})</span></h2>
              {renderGrid(connectedItems, "connected")}
            </section>
          )}

          {(category === "Connected" || availableItems.length > 0) && (
            <section className="integration-section">
              <div className="integration-section-head">
                <h2 className="integration-section-title">{category === "Connected" ? "Connected integrations" : "Available integrations"} <span>({category === "Connected" ? connectedItems.length : availableItems.length})</span></h2>
                {filtering && (
                  <button type="button" className="int-link" onClick={() => { setCategory("All"); setQuery(""); }}>View all integrations <ArrowRight size={14} /></button>
                )}
              </div>
              {renderGrid(category === "Connected" ? connectedItems : availableItems, category === "Connected" ? "connected" : "available")}
            </section>
          )}

          {filtered.length === 0 && (
            <div className="integration-empty">No integrations match “{query || category}”. Try another search or category.</div>
          )}

          <IntegrationRequest />
          </>}
        </div>
      </div>
    </>
  );
}

const PAGE_CSS = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes integration-pulse {
    0%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--lyra-color-primary) 55%, transparent); }
    30%  { box-shadow: 0 0 0 8px color-mix(in srgb, var(--lyra-color-primary) 0%, transparent); }
    100% { box-shadow: 0 0 0 0 transparent; }
  }
  .integration-highlight { border-radius: 16px; animation: integration-pulse 2s ease-out; }
  .integrations-page, .integrations-page * { box-sizing: border-box; font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  .integrations-page { min-height: 100vh; background: var(--lyra-color-surface); color: var(--lyra-color-text); padding: 24px 28px 56px; }
  .integrations-container { width: min(1480px, 100%); margin: 0 auto; }

  .int-hero { position: relative; overflow: hidden; display: flex; align-items: center; justify-content: space-between; gap: 24px; min-height: 230px; padding: 36px 44px; border-radius: 22px; border: 1px solid var(--lyra-color-border);
    background: radial-gradient(120% 140% at 100% 50%, color-mix(in srgb, var(--lyra-color-primary) 34%, transparent) 0%, transparent 62%), linear-gradient(120deg, color-mix(in srgb, var(--lyra-color-primary) 14%, var(--lyra-color-surface)), var(--lyra-color-surface-2)); }
  .int-hero-copy { position: relative; z-index: 1; max-width: 560px; }
  .int-hero-tag { display: inline-block; padding: 6px 14px; border-radius: 999px; background: color-mix(in srgb, var(--lyra-color-primary) 16%, transparent); color: var(--lyra-color-primary); font-size: 13px; font-weight: 600; }
  .integrations-title { margin: 16px 0 0; font-size: 34px; line-height: 1.15; font-weight: 700; letter-spacing: -.025em; color: var(--lyra-color-text); }
  .integrations-subtitle { margin: 10px 0 0; font-size: 15px; line-height: 1.55; color: var(--lyra-color-text-muted); }
  .int-hero-art { position: relative; flex-shrink: 0; width: 420px; height: 180px; margin-right: 30px; }
  .int-hero-tile { position: absolute; width: 66px; height: 66px; display: grid; place-items: center; border-radius: 18px; background: #F4F6FF; box-shadow: 0 10px 28px rgba(8,12,24,.28); }
  .int-hero-tile.center { top: 50%; left: 50%; width: 92px; height: 92px; margin: -46px 0 0 -46px; border-radius: 24px; background: linear-gradient(145deg, var(--lyra-color-primary), color-mix(in srgb, var(--lyra-color-primary) 55%, #0b3d2e)); transform: rotate(4deg); }

  .integration-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 22px 0 6px; flex-wrap: wrap; }
  .int-chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .int-chip { display: inline-flex; align-items: center; gap: 7px; height: 38px; padding: 0 13px; border-radius: 12px; border: 1px solid var(--lyra-color-border); background: var(--lyra-color-surface); color: var(--lyra-color-text); font-size: 13px; font-weight: 500; cursor: pointer; transition: border-color .15s, background .15s; }
  .int-chip:hover { border-color: var(--app-border-strong); }
  .int-chip.active { border-color: var(--lyra-color-primary); background: var(--app-primary-soft); color: var(--lyra-color-text); font-weight: 600; }
  .int-chip b { font-weight: 600; font-size: 12px; padding: 1px 7px; border-radius: 999px; background: var(--app-surface-3); color: var(--lyra-color-text-muted); }
  .int-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--lyra-color-success); }
  .int-tools { display: flex; gap: 10px; margin-left: auto; }
  .integration-search { position: relative; width: 230px; }
  .integration-search input { width: 100%; height: 40px; padding: 0 14px 0 38px; border: 1px solid var(--lyra-color-border); border-radius: 12px; background: var(--lyra-color-surface); color: var(--lyra-color-text); font-size: 13px; outline: none; }
  .integration-search input:focus { border-color: var(--lyra-color-primary); }
  .integration-search input[type="search"] { -webkit-appearance: none; appearance: none; }
  .integration-search input[type="search"]::-webkit-search-cancel-button { -webkit-appearance: none; display: none; }
  .integration-search svg { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: var(--lyra-color-text-muted); }
  .integration-sort { position: relative; display: flex; align-items: center; }
  .integration-sort svg { position: absolute; left: 12px; pointer-events: none; color: var(--lyra-color-text-muted); }
  .integration-sort select { height: 40px; min-width: 120px; padding: 0 12px 0 34px; border: 1px solid var(--lyra-color-border); border-radius: 12px; background: var(--lyra-color-surface); color: var(--lyra-color-text); font-size: 13px; outline: none; cursor: pointer; }

  .integration-section { margin-top: 26px; }
  .integration-section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .integration-section-title { margin: 0 0 14px; font-size: 20px; line-height: 1.3; font-weight: 700; letter-spacing: -.01em; color: var(--lyra-color-text); }
  .integration-section-title span { color: var(--lyra-color-text-muted); font-weight: 500; font-size: 15px; margin-left: 4px; }
  .int-link { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 14px; border: none; background: none; color: var(--lyra-color-primary); font-size: 13px; font-weight: 600; cursor: pointer; }
  .integration-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
  .integration-grid.connected { grid-template-columns: repeat(auto-fill, minmax(440px, 1fr)); }
  .integration-grid-item { min-width: 0; display: flex; }
  .integration-grid-item > * { flex: 1; min-width: 0; }

  /* tile */
  .int-card { position: relative; display: flex; flex-direction: column; gap: 14px; height: 100%; padding: 18px; border-radius: 16px; border: 1px solid var(--lyra-color-border); background: var(--lyra-color-surface); transition: border-color .15s, box-shadow .15s; }
  .int-card:hover { border-color: var(--app-border-strong); box-shadow: 0 6px 20px rgba(8,12,24,.12); }
  .int-head { display: flex; gap: 14px; align-items: flex-start; min-width: 0; }
  .int-icon { flex-shrink: 0; width: 46px; height: 46px; border-radius: 13px; display: grid; place-items: center; }
  .int-text { min-width: 0; display: flex; flex-direction: column; align-items: flex-start; }
  .int-name { font-size: 15px; font-weight: 700; color: var(--lyra-color-text); }
  .int-desc { margin-top: 3px; font-size: 12.5px; line-height: 1.45; color: var(--lyra-color-text-muted); overflow-wrap: anywhere; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .int-pill { margin-top: 10px; padding: 3px 10px; border-radius: 8px; font-size: 11px; font-weight: 600; background: var(--app-surface-3); color: var(--lyra-color-text-muted); }
  .int-pill.popular { background: color-mix(in srgb, #F59E0B 18%, transparent); color: #D97706; }
  .int-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: auto; }
  .int-actions > :not(.int-kebab):not(.int-actions-extra) { flex: 1 1 0; min-width: 0; }
  .int-actions-extra { flex: 0 0 100%; order: 9; }
  .int-btn { height: 38px; padding: 0 14px; border-radius: 10px; border: 1px solid transparent; font-size: 13px; font-weight: 600; font-family: inherit; display: inline-flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer; white-space: nowrap; transition: filter .15s; }
  .int-btn:hover:not(:disabled) { filter: brightness(1.06); }
  .int-btn:disabled { cursor: not-allowed; opacity: .6; }
  .int-btn.primary { background: color-mix(in srgb, var(--lyra-color-primary) 82%, var(--lyra-color-surface)); color: var(--lyra-color-primary-fg, #fff); }
  .int-btn.primary.accent { background: color-mix(in srgb, #A78BFA 85%, var(--lyra-color-surface)); color: #1B1035; }
  .int-btn.ghost { border-color: var(--lyra-color-border); background: var(--lyra-color-surface-2); color: var(--lyra-color-text); }
  .int-btn-group { display: flex; gap: 8px; }
  .int-btn-group > .int-btn:first-child { flex: 1 1 auto; }
  .int-btn.danger { border-color: var(--app-danger-border); background: var(--lyra-color-danger-bg); color: var(--lyra-color-danger); }
  .int-kebab { position: relative; flex: 0 0 auto; }
  .int-kebab-btn { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 10px; border: 1px solid var(--lyra-color-border); background: var(--lyra-color-surface-2); color: var(--lyra-color-text-muted); cursor: pointer; }
  .int-kebab-btn:hover { color: var(--lyra-color-text); border-color: var(--app-border-strong); }
  .int-menu { position: absolute; right: 0; bottom: calc(100% + 6px); z-index: 20; min-width: 170px; padding: 6px; border-radius: 12px; border: 1px solid var(--app-border-strong); background: var(--lyra-color-surface); box-shadow: 0 14px 34px rgba(8,12,24,.28); }
  .int-menu a { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; color: var(--lyra-color-text); font-size: 13px; text-decoration: none; }
  .int-menu a:hover { background: var(--app-primary-soft); }

  /* connected row */
  .int-card.connected { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 16px; padding: 22px 24px; border-color: color-mix(in srgb, var(--lyra-color-success) 35%, var(--lyra-color-border)); }
  .int-card.connected .int-head { flex: 1 1 200px; align-items: center; }
  .int-card.connected .int-desc { display: block; white-space: nowrap; text-overflow: ellipsis; overflow-wrap: normal; }
  .int-card.connected .int-icon { width: 54px; height: 54px; border-radius: 15px; }
  .int-card.connected .int-actions { flex: 0 1 auto; margin-top: 0; flex-wrap: wrap; justify-content: flex-end; }
  .int-card.connected .int-actions > :not(.int-kebab):not(.int-actions-extra) { flex: 0 0 auto; }
  .int-connected { display: inline-flex; align-items: center; gap: 7px; padding: 6px 14px; border-radius: 9px; background: var(--lyra-color-success-bg); color: var(--lyra-color-success); font-size: 13px; font-weight: 600; }
  .int-connected i { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
  .int-card.connected .int-actions-extra { flex-basis: 100%; }
  .int-card.connected .int-btn.danger { width: 38px; padding: 0; }
  .int-card.connected .int-btn-label { display: none; }

  /* modal */
  .int-overlay { position: fixed; inset: 0; z-index: 80; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(8,12,24,.55); }
  .int-modal { width: min(440px, 100%); max-height: 90vh; overflow-y: auto; padding: 22px; border-radius: 16px; border: 1px solid var(--app-border-strong); background: var(--lyra-color-surface); color: var(--lyra-color-text); box-shadow: 0 24px 60px rgba(8,12,24,.4); display: flex; flex-direction: column; gap: 14px; font-family: 'DM Sans', -apple-system, sans-serif; }
  .int-modal * { box-sizing: border-box; font-family: inherit; }
  .int-modal-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
  .int-modal-title { font-size: 17px; font-weight: 700; }
  .int-modal-sub { margin-top: 4px; font-size: 12.5px; line-height: 1.45; color: var(--lyra-color-text-muted); }
  .int-modal .int-kebab-btn { width: 32px; height: 32px; flex-shrink: 0; }
  .int-form { display: flex; flex-direction: column; gap: 12px; }
  .int-modal > div:not(.int-modal-head) { display: flex; flex-direction: column; gap: 8px; }
  .int-field { display: flex; flex-direction: column; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--lyra-color-text); }
  .int-error { padding: 9px 12px; border-radius: 9px; border: 1px solid var(--app-danger-border); background: var(--lyra-color-danger-bg); color: var(--lyra-color-danger); font-size: 12.5px; line-height: 1.4; }

  .integration-empty { margin-top: 28px; border: 1px dashed var(--lyra-color-border); border-radius: 14px; padding: 30px 18px; text-align: center; background: var(--lyra-color-surface-2); color: var(--lyra-color-text-muted); font-size: 13px; }
  .integration-request { margin-top: 32px; border: 1px solid var(--lyra-color-border); background: var(--lyra-color-surface-2); border-radius: 14px; padding: 16px 18px; display: flex; flex-direction: column; gap: 14px; }
  .integration-request-top { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
  .integration-request-form textarea { width: 100%; box-sizing: border-box; resize: vertical; border: 1px solid var(--lyra-color-border); border-radius: 10px; background: var(--lyra-color-surface); color: var(--lyra-color-text); font: inherit; font-size: 13px; padding: 10px 12px; }
  .integration-request-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
  .integration-request button.primary { background: var(--lyra-color-primary); border-color: var(--lyra-color-primary); color: var(--app-on-accent); }
  .integration-request button:disabled { opacity: .55; cursor: default; }
  .integration-request-copy { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .integration-request-icon { width: 36px; height: 36px; border-radius: 10px; background: var(--app-primary-soft); color: var(--lyra-color-primary); display: grid; place-items: center; flex-shrink: 0; }
  .integration-request-title { font-size: 13px; font-weight: 700; color: var(--lyra-color-text); }
  .integration-request-text { font-size: 12px; color: var(--lyra-color-text-muted); margin-top: 2px; }
  .integration-request button { flex-shrink: 0; height: 34px; padding: 0 12px; border: 1px solid var(--lyra-color-border); border-radius: 9px; background: var(--lyra-color-surface); color: var(--lyra-color-text); font-size: 12px; font-weight: 650; }

  @media (max-width: 1100px) { .int-hero-art { display: none; } }
  @media (max-width: 820px) {
    .integrations-page { padding: 20px 18px 44px; }
    .int-hero { padding: 28px 24px; min-height: 0; }
    .integration-grid.connected { grid-template-columns: 1fr; }
  }
  @media (max-width: 560px) {
    .integrations-page { padding: 16px 14px 40px; }
    .integrations-title { font-size: 26px; }
    .int-tools { width: 100%; }
    .integration-search { flex: 1; width: auto; }
    .integration-grid { grid-template-columns: 1fr; gap: 10px; }
    .int-card.connected { padding: 16px; }
    .int-card.connected .int-actions { flex-basis: 100%; justify-content: flex-start; }
  }
`;
